import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, X } from "lucide-react";

/**
 * Excel import button + preview modal.
 * `mode` = "sor" | "boq"
 * Parses:
 *   - SOR: expects columns for IOW Code | Item of Work | UOM | Rate (case-insensitive, headers detected)
 *   - BOQ: expects IOW Code | Description | UOM | Qty | Rate, optionally Category / Sub-category
 *
 * Rows without an IOW code are treated as headers:
 *   - Row with only a big text in "description/item" cell becomes the current CATEGORY (if SL.No present) or SUB-CATEGORY
 * The parser is forgiving to match the Riverwood sheet layout.
 */
export default function ExcelImport({ mode, onImport, buttonLabel = "Import Excel", size = "default" }) {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [wb, setWb] = useState(null);
  const [fileName, setFileName] = useState("");
  const [open, setOpen] = useState(false);
  const [replace, setReplace] = useState(mode === "boq");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setErr("");
    try {
      const buf = await file.arrayBuffer();
      const parsedWb = XLSX.read(buf, { type: "array" });
      const parsed = mode === "sor" ? parseSOR(parsedWb) : parseBOQ(parsedWb);
      if (parsed.length === 0) {
        setErr("No rows detected. Check that the sheet has IOW Code, Item/Description, UOM, Rate columns.");
      }
      setWb(parsedWb);
      setRows(parsed);
      setOpen(true);
    } catch (er) {
      setErr(String(er.message || er));
      setOpen(true);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onImport(rows, replace, wb);
      setOpen(false);
      setRows([]);
      setWb(null);
      setFileName("");
    } catch (er) {
      setErr(er?.response?.data?.detail || String(er.message || er));
    } finally { setBusy(false); }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFile}
        className="hidden"
        data-testid={`excel-file-input-${mode}`}
      />
      <Button
        data-testid={`excel-import-btn-${mode}`}
        onClick={pick}
        variant="outline"
        size={size}
        className="rounded-full"
      >
        <Upload className="h-4 w-4 mr-1.5" /> {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setRows([]); }}>
        <DialogContent className="max-w-3xl p-0" data-testid={`excel-preview-dialog-${mode}`}>
          <div className="p-4 border-b border-slate-100">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-[#F97316]" /> Import preview
              </DialogTitle>
              <div className="text-xs text-slate-500 mt-1 truncate">{fileName}</div>
            </DialogHeader>
          </div>

          {err && (
            <div className="mx-4 mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2 flex items-start gap-2" data-testid={`excel-error-${mode}`}>
              <X className="h-4 w-4 mt-0.5 shrink-0" /> {err}
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="px-4 py-2 flex items-center justify-between text-sm">
                <div className="text-slate-600">
                  <span className="font-semibold text-[#0D3B66]" data-testid={`excel-row-count-${mode}`}>{rows.length}</span> rows detected
                </div>
                {mode === "boq" && (
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="excel-replace-checkbox"
                      checked={replace}
                      onChange={(e) => setReplace(e.target.checked)}
                    /> Replace existing draft lines
                  </label>
                )}
              </div>
              <div className="max-h-[50vh] overflow-auto border-t border-slate-100">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 font-semibold">IOW</th>
                      <th className="px-3 py-2 font-semibold">{mode === "sor" ? "Item" : "Description"}</th>
                      <th className="px-3 py-2 font-semibold">UOM</th>
                      {mode === "boq" && <th className="px-3 py-2 font-semibold text-right">Qty</th>}
                      <th className="px-3 py-2 font-semibold text-right">Rate</th>
                      {mode === "boq" && <th className="px-3 py-2 font-semibold">Category</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 200).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono">{r.iow_code}</td>
                        <td className="px-3 py-1.5">{mode === "sor" ? r.item_of_work : r.description}</td>
                        <td className="px-3 py-1.5">{r.uom}</td>
                        {mode === "boq" && <td className="px-3 py-1.5 text-right">{r.qty}</td>}
                        <td className="px-3 py-1.5 text-right">{r.rate}</td>
                        {mode === "boq" && <td className="px-3 py-1.5 text-slate-500">{r.category}{r.sub_category ? ` › ${r.sub_category}` : ""}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 200 && <div className="p-3 text-center text-xs text-slate-500">Showing first 200 of {rows.length}</div>}
              </div>
            </>
          )}

          <DialogFooter className="p-4 border-t border-slate-100">
            <Button
              data-testid={`excel-confirm-import-${mode}`}
              onClick={confirm}
              disabled={busy || rows.length === 0}
              className="bg-[#0D3B66] hover:bg-[#114B82]"
            >
              {busy ? "Importing…" : `Import ${rows.length} rows`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---- Parsers ----

function findHeaderRow(rows) {
  // Look for a row containing "IOW Code" or "IOW". Rate may be on same row OR on next row (Riverwood layout).
  for (let i = 0; i < Math.min(rows.length, 40); i++) {
    const row = rows[i].map((c) => String(c || "").trim().toLowerCase());
    const hasIow = row.some((c) => c.includes("iow"));
    if (!hasIow) continue;
    const hasRate = row.some((c) => c === "rate" || c.startsWith("rate"));
    if (hasRate) return { idx: i, row, subRow: null };
    // Check next row for qty / rate / amount (merged sub-header)
    const nxt = (rows[i + 1] || []).map((c) => String(c || "").trim().toLowerCase());
    const nxtHasRate = nxt.some((c) => c === "rate");
    const nxtHasQty = nxt.some((c) => c === "qty" || c.includes("quantity"));
    if (nxtHasRate || nxtHasQty) return { idx: i, row, subRow: nxt };
  }
  return null;
}

function colIndex(headerRow, patterns) {
  for (const p of patterns) {
    const idx = headerRow.findIndex((c) => p.test(String(c || "").toLowerCase()));
    if (idx !== -1) return idx;
  }
  return -1;
}

function parseSOR(wb) {
  const results = [];
  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    const h = findHeaderRow(rows);
    if (!h) continue;
    const hRow = h.row;
    const iowCol = colIndex(hRow, [/iow.*code/, /^iow$/, /code/]);
    const itemCol = colIndex(hRow, [/item.*work/, /description/, /item/]);
    const uomCol = colIndex(hRow, [/^uom$/, /unit/]);
    const rateCol = colIndex(hRow, [/^rate$/]);
    if (iowCol < 0 || rateCol < 0) continue;
    for (let i = h.idx + 1; i < rows.length; i++) {
      const r = rows[i];
      if (!r) continue;
      const code = r[iowCol];
      const name = r[itemCol];
      const rate = r[rateCol];
      if (code == null || name == null) continue;
      const codeStr = String(code).trim();
      if (!codeStr || codeStr.toLowerCase() === "iow code") continue;
      const rateNum = Number(rate) || 0;
      results.push({
        iow_code: codeStr,
        item_of_work: String(name).trim(),
        uom: uomCol >= 0 && r[uomCol] != null ? String(r[uomCol]).trim() : "",
        rate: rateNum,
        category: "",
        sub_category: "",
      });
    }
    if (results.length > 0) break; // First matching sheet
  }
  return results;
}

function parseBOQ(wb) {
  // Try to find a sheet named like Budget BOQ, else scan all
  let sheetNames = wb.SheetNames.filter((n) => /boq|budget/i.test(n));
  if (sheetNames.length === 0) sheetNames = wb.SheetNames;
  for (const sheetName of sheetNames) {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    const h = findHeaderRow(rows);
    if (!h) continue;
    const hRow = h.row;
    const sub = h.subRow;
    const slCol = colIndex(hRow, [/^sl\.?\s*no/, /^s\.?no/, /^sr/]);
    const iowCol = colIndex(hRow, [/iow.*code/, /^iow$/, /^code$/]);
    const descCol = colIndex(hRow, [/description/, /item.*work/, /item/]);
    const uomCol = colIndex(hRow, [/^uom$/, /^unit$/]);
    let qCol = colIndex(hRow, [/^qty$/, /quantity/, /budget.*qty/]);
    let rCol = colIndex(hRow, [/^rate$/]);
    // Additional columns from Riverwood layout — sub-header row indices
    let gfcCol = -1, jcrQtyCol = -1, jcrRateCol = -1, raCol = -1;
    if ((qCol < 0 || rCol < 0) && sub) {
      // Budget block: sub row has qty, rate, amount at columns 4,5,6 (typical Riverwood)
      if (qCol < 0) qCol = sub.findIndex((c) => c === "qty" || c.includes("quantity"));
      if (rCol < 0) rCol = sub.findIndex((c) => c === "rate");
    }
    if (iowCol < 0 || descCol < 0) continue;
    if (qCol < 0 || rCol < 0) continue;
    if (sub) {
      // GFC Qty and JCR Qty/Rate: subsequent columns
      gfcCol = sub.findIndex((c, i) => i > rCol && (c === "gfc qty" || c.includes("gfc")));
      // JCR Qty/Rate: search after the amount column
      for (let i = rCol + 1; i < sub.length; i++) {
        if (sub[i] === "qty" && jcrQtyCol === -1) jcrQtyCol = i;
        else if (sub[i] === "rate" && jcrRateCol === -1 && jcrQtyCol !== -1 && i > jcrQtyCol) { jcrRateCol = i; break; }
      }
    }
    // RA column is on the main header row (last column typically), value "Y"/"N"
    raCol = hRow.findIndex((c) => c === "ra");

    const results = [];
    let currentCategory = "General";
    let currentSubCategory = "";
    const startIdx = h.idx + (sub ? 2 : 1);
    for (let i = startIdx; i < rows.length; i++) {
      const r = rows[i];
      if (!r) continue;
      const code = r[iowCol];
      const desc = r[descCol];
      const qty = r[qCol];
      const rate = r[rCol];
      const sl = slCol >= 0 ? r[slCol] : null;

      if (sl != null && (code == null || String(code).trim() === "") && desc && String(desc).trim()) {
        currentCategory = String(desc).trim();
        currentSubCategory = "";
        continue;
      }
      if ((code == null || String(code).trim() === "") && desc && String(desc).trim() && (qty == null || String(qty).trim() === "")) {
        currentSubCategory = String(desc).trim();
        continue;
      }
      if (code == null || String(code).trim() === "") continue;
      const codeStr = String(code).trim();
      if (codeStr.toLowerCase().includes("iow")) continue;
      const raFlag = raCol >= 0 && r[raCol] != null ? String(r[raCol]).trim().toUpperCase() === "Y" : false;
      results.push({
        iow_code: codeStr,
        description: String(desc || "").trim(),
        uom: uomCol >= 0 && r[uomCol] != null ? String(r[uomCol]).trim() : "",
        category: currentCategory,
        sub_category: currentSubCategory,
        qty: Number(qty) || 0,
        rate: Number(rate) || 0,
        gfc_qty: gfcCol >= 0 ? (Number(r[gfcCol]) || 0) : 0,
        jcr_qty: jcrQtyCol >= 0 ? (Number(r[jcrQtyCol]) || 0) : 0,
        jcr_rate: jcrRateCol >= 0 ? (Number(r[jcrRateCol]) || 0) : 0,
        has_ra: raFlag,
      });
    }
    if (results.length > 0) return results;
  }
  return [];
}

/**
 * Parse Rate Analysis (RA working) sheet.
 * Returns a map: { [iow_code]: [{type, resource, resource_uom, resource_rate, co_eff}, ...] }
 * Header row contains: ERP code | IOW Code | Type | IOW Unit | IOW | Co-Eff | Resources | Resource UOM | Resource Rate | Cost / Unit | IOW Qty | Total resource | Resource value
 */
export function parseRA(wb) {
  const raMap = {};
  const sheetNames = wb.SheetNames.filter((n) => /ra|rate.*analysis/i.test(n));
  const targets = sheetNames.length > 0 ? sheetNames : wb.SheetNames;
  for (const sheetName of targets) {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    // Find header row: has "IOW Code" and "Co-Eff" and "Resources"
    let hIdx = -1;
    for (let i = 0; i < Math.min(rows.length, 40); i++) {
      const r = (rows[i] || []).map((c) => String(c || "").trim().toLowerCase());
      if (r.some((c) => c.includes("iow code")) && r.some((c) => c.includes("co-eff") || c.includes("coeff")) && r.some((c) => c.includes("resource"))) {
        hIdx = i; break;
      }
    }
    if (hIdx < 0) continue;
    const hRow = rows[hIdx].map((c) => String(c || "").trim().toLowerCase());
    const iowCol = colIndex(hRow, [/iow.*code/]);
    const typCol = colIndex(hRow, [/^type$/]);
    const coeffCol = colIndex(hRow, [/co-?eff/, /coefficient/]);
    const resCol = colIndex(hRow, [/^resource$/, /^resources$/]);
    const resUomCol = colIndex(hRow, [/resource.*uom/, /res.*uom/]);
    const resRateCol = colIndex(hRow, [/resource.*rate/, /res.*rate/]);
    if (iowCol < 0 || typCol < 0 || coeffCol < 0 || resCol < 0) continue;

    let currentIow = null;
    for (let i = hIdx + 1; i < rows.length; i++) {
      const r = rows[i];
      if (!r) continue;
      const iow = r[iowCol];
      if (iow != null && String(iow).trim()) currentIow = String(iow).trim();
      if (!currentIow) continue;
      const typ = r[typCol];
      const res = r[resCol];
      if (typ == null || res == null) continue;
      const typStr = String(typ).trim();
      if (!typStr || typStr.toLowerCase() === "type") continue;
      const entry = {
        type: typStr,
        resource: String(res).trim(),
        resource_uom: resUomCol >= 0 && r[resUomCol] != null ? String(r[resUomCol]).trim() : "",
        resource_rate: Number(r[resRateCol]) || 0,
        co_eff: Number(r[coeffCol]) || 0,
      };
      if (!raMap[currentIow]) raMap[currentIow] = [];
      raMap[currentIow].push(entry);
    }
    if (Object.keys(raMap).length > 0) break;
  }
  return raMap;
}
